<template>
  <div class="plans-modal" role="alert">
    <div class="plans-modal__wrapper">
      <button class="plans-modal__close" @click="$emit('toggle-upgrade-modal')">
        <XIcon class="plans-modal__close--icon" />
      </button>
      <div v-if="isLoading" class="plans-modal__loader-wrapper">
        <ActionLoader name="line-scale" color="#ef7d22" />
      </div>
      <template v-else>
        <div class="plans-modal__title-wrapper">
          <h5 class="plans-modal__title">
            Select a subscription to calculate new costs
          </h5>
        </div>
        <div class="plans-modal__plans-container">
          <div
            :class="[
              'plans-modal__plans-wrapper',
              !allFundamentalsPlansShowed &&
                'plans-modal__plans-wrapper--cards',
            ]"
          >
            <div class="plans-modal__header-wrapper">
              <Plan
                v-for="plan in availableFundamentalsPlans"
                :key="plan.id"
                :plan="plan"
                :hasMonthlyPlan="hasMonthlyPlan"
                :selectedPlan="selectedPlan"
                :moreInfoUrl="`${infoUrl}/fundamentals-faq`"
                @update-selected-plan="updateSelectedPlan"
              />
              <Plan
                v-for="plan in availablePremiumPlans"
                :key="plan.id"
                :plan="plan"
                :hasMonthlyPlan="hasMonthlyPlan"
                :selectedPlan="selectedPlan"
                :moreInfoUrl="`${infoUrl}/premium-faq`"
                @update-selected-plan="updateSelectedPlan"
              />
            </div>
          </div>
        </div>
        <template
          v-if="selectedPlan && selectedPlan.name"
          class="plans-modal__confirmation-section"
        >
          <div
            v-if="isComparisonLoading"
            class="plans-modal__confirmation-wrapper plans-modal__confirmation-wrapper--loading"
          >
            <div class="plans-modal__comparison-loader">
              <ActionLoader name="ball-clip-rotate" color="#ef7d22" />
            </div>
          </div>
          <div v-else class="plans-modal__confirmation-container">
            <div class="plans-modal__confirmation-wrapper">
              <div class="plans-modal__calculations">
                <div
                  v-for="(item, index) in comparisonInfo"
                  :key="index"
                  class="plans-modal__calculation"
                >
                  <div class="plans-modal__calculation-title">
                    {{ item.title }}
                  </div>
                  <div class="plans-modal__calculation-info">
                    {{ item.info }}
                  </div>
                </div>
              </div>
              <div class="plans-modal__btn-wrapper">
                <button
                  :disabled="isConfirmBtnDisabled"
                  class="btn btn--primary"
                  @click="confirmUpgrade()"
                >
                  Confirm and switch
                </button>
              </div>
            </div>
            <p
              class="plans-modal__offer-warning plans-modal__offer-warning--desktop"
            >
              ** Tax calculated at checkout. This offer is based on the time it
              was generated. | Offer ID:
              {{ comparison.offerId }}
            </p>
            <div class="plans-modal__btn-wrapper--mobile">
              <button
                :disabled="isConfirmBtnDisabled"
                class="btn btn--primary"
                @click="confirmUpgrade()"
              >
                Confirm and switch
              </button>
            </div>
            <p
              class="plans-modal__offer-warning plans-modal__offer-warning--mobile"
            >
              ** Tax calculated at checkout. This offer is based on the time it
              was generated. | Offer ID:
              {{ comparison.offerId }}
            </p>
          </div>
        </template>
      </template>
    </div>
  </div>
</template>

<script>
import { mapState, mapGetters } from 'vuex'
import ActionLoader from '@/components/shared/ActionLoader.vue'
import { XIcon } from 'vue-feather-icons'
import CONSTANTS from '@/constants'
import Plan from './upgrade-plan-modal/Plan.vue'

export default {
  name: 'UpgradePlanModal',
  components: {
    ActionLoader,
    Plan,
    XIcon,
  },
  props: {
    subscriptionId: {
      type: String,
      required: true,
    },
    couponCode: {
      type: String,
      required: false,
    },
    isPremiumOnlyModal: {
      type: Boolean,
      required: false,
      default() {
        return false
      },
    },
  },
  data() {
    return {
      isLoading: true,
      isComparisonLoading: true,
      isConfirmBtnDisabled: false,
      selectedPlan: {},
      infoUrl: `https://${process.env.VUE_APP_INFO_URL}`,
    }
  },
  computed: {
    ...mapState({
      currentPlanComparison: state => state.subscriptions.currentPlanComparison,
      currentSubscriptions: state => state.subscriptions.currentSubscriptions,
      premiumPlanCode: state => state.subscriptions.premiumPlanCode,
    }),
    ...mapGetters({
      comparison: 'subscriptions/currentPlanComparisonBreakdown',
      availablePlans: 'subscriptions/currentAvailablePlans',
      activePlans: 'subscriptions/activePlans',
    }),
    planToBeUpgraded() {
      return this.currentSubscriptions.find(
        sub => sub.id === this.subscriptionId
      )
    },
    hasMonthlyPlan() {
      if (this.planToBeUpgraded) {
        const { plan_code } = this.planToBeUpgraded
        return plan_code?.length && plan_code === CONSTANTS.PLANS.MONTHLY
      }
      return false
    },
    availableFundamentalsPlans() {
      return this.availablePlans
        ?.filter(
          plan =>
            (plan.merchant_id === CONSTANTS.PLANS.MONTHLY &&
              this.currentPlan !== CONSTANTS.PLANS.ANNUAL) ||
            plan.merchant_id === CONSTANTS.PLANS.ANNUAL
        )
        .sort((a, b) => b.price.localeCompare(a.price))
    },
    currentPlan() {
      const filteredPlans = this.activePlans.filter(
        plan => plan.plan_code !== CONSTANTS.PLANS.ALL.skill_dive_annual
      )
      return filteredPlans && filteredPlans.length
        ? filteredPlans[0]?.plan_code
        : null
    },
    availablePremiumPlans() {
      return this.availablePlans?.filter(
        plan =>
          [
            CONSTANTS.PLANS.PREMIUM,
            CONSTANTS.PLANS.PREMIUM_PLANS.INE_PREMIUM_DEFENSE,
            CONSTANTS.PLANS.PREMIUM_PLANS.INE_PREMIUM_MONTHLY,
          ].includes(plan.merchant_id) && plan.show
      )
    },
    allFundamentalsPlansShowed() {
      return this.availableFundamentalsPlans.length === 2
    },
    comparisonInfo() {
      const {
        newPrice,
        dueToday,
        creditForRenewal,
        renewalDate,
        renewalTotal,
      } = this.comparison
      const renewal = `${renewalDate} for ${renewalTotal}`
      return [
        {
          title: 'New Price:',
          info: newPrice,
        },
        {
          title: 'Due Today to Upgrade:',
          info: dueToday,
        },
        {
          title: 'Credit available for renewal:',
          info: creditForRenewal,
        },
        {
          title: 'Next Renewal:',
          info: renewal,
        },
      ]
    },
    defaultSelectedPlan() {
      return this.availablePlans.find(
        plan =>
          plan.merchant_id === CONSTANTS.PLANS.PREMIUM_PLANS.INE_PREMIUM
      )
    },
    premiumPlan() {
      return this.availablePlans.find(
        plan => plan.merchant_id === this.premiumPlanCode
      )
    },
  },
  async created() {
    const body = document.querySelector('body')
    body.classList.add('modal-open')
    this.isLoading = true
    await this.getAvailablePlans()
    if (this.subscriptionId) {
      await this.$store.dispatch(
        'subscriptions/getValidPlans',
        this.subscriptionId
      )
    }
    this.updateSelectedPlan(this.defaultSelectedPlan)
    this.isLoading = false
  },
  destroyed() {
    const body = document.querySelector('body')
    body.classList.remove('modal-open')
  },
  methods: {
    async getAvailablePlans() {
      await this.$store.dispatch('subscriptions/fetchAvailablePlans')
    },
    async updateSelectedPlan(plan) {
      this.selectedPlan = plan
      this.isComparisonLoading = true
      try {
        const { merchant_id } = plan
        const payload = {
          id: this.subscriptionId,
          plan_code: merchant_id,
        }
        if (
          this.couponCode &&
          this.couponCode.length &&
          this.isPremiumOnlyModal
        ) {
          payload.coupon_code = this.couponCode
        }
        await this.$store.dispatch('subscriptions/getPlanComparison', payload)
      } catch (error) {
        if (this.isPremiumOnlyModal) return this.$emit('toggle-upgrade-modal')
        this.selectedPlan = {}
      } finally {
        this.isComparisonLoading = false
      }
    },
    confirmUpgrade() {
      this.isConfirmBtnDisabled = true
      this.$toasted.info(
        'Please confirm that you agree to the charges and that you are ready to upgrade',
        {
          iconPack: 'material',
          position: 'bottom-center',
          icon: 'info',
          class: 'upgrade__toast',
          action: [
            {
              text: 'Confirm',
              onClick: (e, toastObject) => {
                toastObject.goAway(0)
                this.isConfirmBtnDisabled = false
                this.upgradePlan()
              },
              class: 'upgrade__toast--confirm',
            },
            {
              text: 'Cancel',
              onClick: (e, toastObject) => {
                toastObject.goAway(0)
                this.isConfirmBtnDisabled = false
              },
              class: 'upgrade__toast--cancel',
            },
          ],
        }
      )
    },
    async upgradePlan() {
      this.isLoading = true
      try {
        const { merchant_id } = this.selectedPlan
        const payload = {
          id: this.subscriptionId,
          plan_code: merchant_id,
        }
        if (
          this.couponCode &&
          this.couponCode.length &&
          this.isPremiumOnlyModal
        ) {
          payload.coupon_code = this.couponCode
        }
        await this.$store.dispatch('subscriptions/upgradePlan', payload)
        this.$store.dispatch('subscriptions/isUpgradePlanAllowed', false)
        this.isLoading = false
        this.$emit('toggle-upgrade-modal')
        if (
          this.couponCode &&
          this.couponCode.length &&
          this.isPremiumOnlyModal
        ) {
          this.$router.push({
            name: 'user-dashboard',
            params: { mode: 'subscriptions' },
          })
        }
      } catch (error) {
        this.isLoading = false
      }
    },
  },
}
</script>
